import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { AuthState, AuthUser } from '../types/auth.types';
import { httpClient } from '@/shared/services/HttpClient';
import { chatService } from '@/shared/services/ChatService';
import { authService } from '../services/GCPAuthService';
import { now, addTime, hasExpired } from '@/shared/utils';

/**
 * Authentication Store
 *
 * Manages authentication state including user information and session expiry.
 * The actual auth token is stored as an HttpOnly cookie by the backend
 * and is never accessible via JavaScript.
 *
 * Dependencies:
 * - httpClient: Sets/clears userId for API requests
 * - authService: GCP authentication service
 *
 * Used by:
 * - useAuth hook: Provides centralized logout that clears all stores
 * - AuthService: Calls setAuth/clearAuth during login/logout
 * - Various components for auth state checks
 *
 * Important: This store does NOT clear other stores on logout.
 * Store clearing is handled by the centralized logout in useAuth hook.
 */

interface AuthActions {
  setAuth: (token: string, user: AuthUser, expiresIn?: number) => void;
  clearAuth: () => void;
  setToken: (token: string) => void;
  setTokenWithExpiry: (token: string, expiresIn?: number) => void;
  setUser: (user: AuthUser) => void;
  setIsAuthenticated: (value: boolean) => void;
  setIsLoggingOut: (value: boolean) => void;
  checkAuthExpiry: () => boolean;
  getTokenExpiry: () => number | null;
  refreshAuthToken: () => Promise<void>;
  initializeFromStoredToken: () => Promise<void>;
  refreshCurrentUser: () => Promise<void>;
  handleAuthenticationError: () => void;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  expiresAt: null,
  isLoggingOut: false,
};

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        ...initialState,

        // Actions with stable references
        setAuth: (_token: string, user: AuthUser, expiresIn?: number) => {
          // GCP tokens default to 7 days (604800 seconds) if not specified
          const actualExpiresIn = expiresIn || 604800;
          const expiresAt = addTime(now(), actualExpiresIn * 1000).getTime();

          // Token is now in HttpOnly cookie — just set userId for headers
          httpClient.resetSessionState();
          httpClient.setUserId(user.email);
          set(
            {
              isAuthenticated: true,
              user,
              token: null, // Token is in HttpOnly cookie, not stored in state
              expiresAt,
            },
            false,
            'setAuth'
          );
        },

        clearAuth: () => {
          // Clear userId from HTTP client (token cookie is cleared by backend)
          httpClient.setUserId(null);

          // Reset to initial state completely
          set(initialState, false, 'clearAuth');
        },

        setToken: (_token: string) => {
          // Token is in HttpOnly cookie — only update expiry tracking
          set({ token: null }, false, 'setToken');
        },

        setTokenWithExpiry: (_token: string, expiresIn?: number) => {
          const actualExpiresIn = expiresIn || 604800;
          const expiresAt = addTime(now(), actualExpiresIn * 1000).getTime();

          // Token is in HttpOnly cookie — only track expiry
          httpClient.resetSessionState();
          set({ token: null, expiresAt }, false, 'setTokenWithExpiry');
        },

        setUser: (user: AuthUser) => {
          set({ user }, false, 'setUser');
        },

        setIsAuthenticated: (value: boolean) => {
          set({ isAuthenticated: value }, false, 'setIsAuthenticated');
        },

        setIsLoggingOut: (value: boolean) => {
          set({ isLoggingOut: value }, false, 'setIsLoggingOut');
        },

        checkAuthExpiry: () => {
          const state = get();
          if (!state.expiresAt) return false;

          const isExpired = hasExpired(state.expiresAt);
          if (isExpired && state.isAuthenticated) {
            get().clearAuth();
          }
          return !isExpired;
        },

        getTokenExpiry: () => {
          const state = get();
          return state.expiresAt;
        },

        refreshAuthToken: async () => {
          try {
            const newToken = await authService.refreshToken();
            if (newToken) {
              const user: AuthUser = {
                uid: newToken.user.email,
                email: newToken.user.email,
                name: newToken.user.username || newToken.user.email,
                displayName:
                  `${newToken.user.firstName || ''} ${newToken.user.lastName || ''}`.trim() ||
                  newToken.user.username,
                emailVerified: true,
                providerId: 'gcp-identity-platform',
                defaultAssistant: newToken.user.defaultAssistant ?? null,
                profilePhotoUrl: newToken.user.profilePhotoUrl ?? null,
                gtcRoles: newToken.user.gtcRoles,
                tenantSlug: newToken.user.tenantSlug ?? null,
                subscriptionTier: newToken.user.subscriptionTier ?? null,
                gtcTenantAssignment: newToken.user.gtcTenantAssignment ?? null,
              };

              get().setAuth('', user);
            } else {
              get().clearAuth();
            }
          } catch (error) {
            console.error('[AuthStore] Failed to refresh token:', error);
            get().clearAuth();
          }
        },

        initializeFromStoredToken: async () => {
          // On rehydration, verify auth with backend using HttpOnly cookie
          try {
            const currentUser = await authService.getCurrentUser();
            if (currentUser === null) {
              if (!authService.getStoredToken()) {
                get().clearAuth();
              }
            } else {
              const updatedUser: AuthUser = {
                uid: currentUser.email,
                email: currentUser.email,
                name: currentUser.username || currentUser.email,
                displayName:
                  `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
                  currentUser.username,
                emailVerified: true,
                providerId: 'gcp-identity-platform',
                defaultAssistant: currentUser.defaultAssistant ?? null,
                profilePhotoUrl: currentUser.profilePhotoUrl ?? null,
                gtcRoles: currentUser.gtcRoles,
                tenantSlug: currentUser.tenantSlug ?? null,
                subscriptionTier: currentUser.subscriptionTier ?? null,
                gtcTenantAssignment: currentUser.gtcTenantAssignment ?? null,
              };
              get().setAuth('', updatedUser);
              // Fetch CSRF token so POST requests work after page refresh
              httpClient.fetchCsrfToken().catch(() => {});
            }
          } catch {
            // Network error - don't clear auth, user might be offline
          }
        },

        refreshCurrentUser: async () => {
          try {
            const currentUser = await authService.getCurrentUser();
            if (!currentUser) {
              return;
            }

            const user: AuthUser = {
              uid: currentUser.id,
              email: currentUser.email,
              name: currentUser.username || currentUser.email,
              displayName:
                `${currentUser.firstName || ''} ${currentUser.lastName || ''}`.trim() ||
                currentUser.username ||
                currentUser.email,
              emailVerified: true,
              providerId: 'gcp-identity-platform',
              defaultAssistant: currentUser.defaultAssistant ?? null,
              profilePhotoUrl: currentUser.profilePhotoUrl ?? null,
              gtcRoles: currentUser.gtcRoles,
              tenantSlug: currentUser.tenantSlug ?? null,
              subscriptionTier: currentUser.subscriptionTier ?? null,
              gtcTenantAssignment: currentUser.gtcTenantAssignment ?? null,
            };

            set({ user }, false, 'refreshCurrentUser');
          } catch (error) {
            console.error('[AuthStore] Failed to refresh current user:', error);
          }
        },

        handleAuthenticationError: () => {
          // Clear auth state immediately
          get().clearAuth();

          // Clear HTTP client user context
          httpClient.setUserId(null);

          // Cancel all pending requests to prevent further errors
          httpClient.cancelAllRequests();
        },
      }),
      {
        name: 'auth-store',
        partialize: state => ({
          isAuthenticated: state.isAuthenticated,
          user: state.user,
          // token is NOT persisted — it's in HttpOnly cookie
          expiresAt: state.expiresAt,
        }),
        onRehydrateStorage: () => state => {
          // Restore userId to HTTP client on rehydration
          if (state?.isAuthenticated) {
            if (state?.user?.email) {
              httpClient.setUserId(state.user.email);
              chatService.setUserId(state.user.email);
            }
            // Check if session is expired
            state.checkAuthExpiry();
            // Verify auth with backend using HttpOnly cookie
            state.initializeFromStoredToken();
          }
        },
      }
    )
  )
);
